import React, { useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { BrandingProvider } from "./context/BrandingContext";
import { ThemeProvider } from "./context/ThemeContext";
import { WebSocketProvider } from "./context/WebSocketContext";
import { LogoProvider } from "./context/LogoContext";

// Auth Pages
import LoginPage from "./pages/auth/LoginPage";
import ForgotPasswordPage from "./pages/auth/ForgotPasswordPage";
import ResetPasswordPage from "./pages/auth/ResetPasswordPage";
import NotFoundPage from "./pages/auth/NotFoundPage";

// Hub Pages
import HubDashboard from "./pages/hub/HubDashboard";
import HubSettingsPage from "./pages/hub/HubSettingsPage";
import HubUsersPage from "./pages/hub/HubUsersPage";
import HubSystemPage from "./pages/hub/HubSystemPage";
import ConnectionHealthPage from "./pages/hub/ConnectionHealthPage";
import PhoneNumbersPage from "./pages/hub/PhoneNumbersPage";
import SipExtensionsPage from "./pages/hub/SipExtensionsPage";
import SipCallLogsPage from "./pages/hub/SipCallLogsPage";
import UploadQuarantinePage from "./pages/hub/UploadQuarantinePage";

// EMS Pages
import DashboardPage from "./pages/ems/DashboardPage";
import ExercisesPage from "./pages/ems/ExercisesPage";
import ExerciseDetailPage from "./pages/ems/ExerciseDetailPage";
import ControlRoomPage from "./pages/ems/ControlRoomPage";
import UsersPage from "./pages/ems/UsersPage";
import InjectsPage from "./pages/ems/InjectsPage";
import TimeTrackingPage from "./pages/ems/TimeTrackingPage";
import ReportsPage from "./pages/ems/ReportsPage";
import AuditLogsPage from "./pages/ems/AuditLogsPage";
import SettingsPage from "./pages/ems/SettingsPage";
import OrganisationPage from "./pages/ems/OrganisationPage";
import ObjectivesPage from "./pages/ems/ObjectivesPage";
import InjectDatabasePage from "./pages/ems/InjectDatabasePage";
import MessagesPage from "./pages/ems/MessagesPage";
import PBXAdminPage from "./pages/ems/PBXAdminPage";
import ParticipantPhonePage from "./pages/ems/ParticipantPhonePage";
import MediaPortalPage from "./pages/ems/MediaPortalPage";
import WorkshopPreflightPage from "./pages/ems/WorkshopPreflightPage";
import LiveWorkshopDashboard from "./pages/ems/LiveWorkshopDashboard";

// Media Portal Pages
import MPDashboardPage from "./pages/mp/MPDashboardPage";
import MPExercisesPage from "./pages/mp/MPExercisesPage";
import MPSocialPostsPage from "./pages/mp/MPSocialPostsPage";
import MPNewsPostsPage from "./pages/mp/MPNewsPostsPage";
import MPMediaPage from "./pages/mp/MPMediaPage";
import MPPlatformsPage from "./pages/mp/MPPlatformsPage";
import MPUsersPage from "./pages/mp/MPUsersPage";
import MPAuditPage from "./pages/mp/MPAuditPage";
import MPSettingsPage from "./pages/mp/MPSettingsPage";
import MPPublicDisplayPage from "./pages/mp/MPPublicDisplayPage";
import MPExerciseDashboard from "./pages/mp/MPExerciseDashboard";
import MPArticleDetailPage from "./pages/mp/MPArticleDetailPage";
import MPGuestPublicDisplayPage from "./pages/mp/MPGuestPublicDisplayPage";

// V15.0 — Logistics & Spend Command (third Hub app, sibling to EMS/MP).
import ExerciseManagementPage from "./pages/logistics/ExerciseManagementPage";
import LogisticsHomePage from "./pages/logistics/LogisticsHomePage";
import LogisticsWalletPage from "./pages/logistics/LogisticsWalletPage";
import LogisticsAccommodationPage from "./pages/logistics/LogisticsAccommodationPage";
import LogisticsFoodPage from "./pages/logistics/LogisticsFoodPage";
import LogisticsTransportPage from "./pages/logistics/LogisticsTransportPage";
import LogisticsFlightsPage from "./pages/logistics/LogisticsFlightsPage";
import LogisticsProcurementPage from "./pages/logistics/LogisticsProcurementPage";

import "./App.css";
import { PBXProvider } from "./context/PBXContext";
import { SIPProvider } from "./context/SIPContext";
import FloatingPhone from "./components/ems/FloatingPhone";

// Google OAuth is disabled; keep callback route but redirect to login.
const AuthCallback = () => {
  const navigate = useNavigate();

  useEffect(() => {
    navigate('/login', { replace: true });
  }, [navigate]);

  return null;
};

const ProtectedRoute = ({ children, allowedRoles = [], allowedMpRoles = [] }) => {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
          <p className="mt-4 text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // V14.7 — Check EMS roles first (source of truth). For users who have ANY
  // recognised EMS role, mp_roles is ignored — prevents stale `mp_roles`
  // carryovers from elevating a participant into observer/admin pages.
  const RECOGNISED_EMS = ['super_admin', 'hub_admin', 'simcel', 'controller', 'observer', 'participant'];
  const emsRoles = user.roles || [];
  const userHasAnyEmsRole = emsRoles.some(r => RECOGNISED_EMS.includes(r));
  const hasEmsRole = allowedRoles.length === 0 || allowedRoles.some(role => emsRoles.includes(role));
  // mp_roles only consulted for legacy MP-only users (no EMS role at all)
  const hasMpRole = !userHasAnyEmsRole && allowedMpRoles.length > 0
    && allowedMpRoles.some(role => (user.mp_roles || []).includes(role));

  if (allowedRoles.length > 0 && !hasEmsRole && !hasMpRole) {
    // Participants attempting to reach MP admin pages get redirected to their
    // kiosk; everyone else falls back to the Hub home.
    if (emsRoles.includes('participant')) {
      return <Navigate to="/mp/public-display" replace />;
    }
    return <Navigate to="/hub" replace />;
  }

  return children;
};

const AppRouter = () => {
  const location = useLocation();

  // Check URL fragment for session_id synchronously during render
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      
      <Route path="/dashboard" element={
        <ProtectedRoute>
          <DashboardPage />
        </ProtectedRoute>
      } />
      
      <Route path="/exercises" element={
        <ProtectedRoute allowedRoles={['super_admin','simcel','observer']}>
          <ExercisesPage />
        </ProtectedRoute>
      } />

      {/* Participant: phone-only dedicated page */}
      <Route path="/phone" element={
        <ProtectedRoute allowedRoles={['participant']}>
          <ParticipantPhonePage />
        </ProtectedRoute>
      } />

      {/* Legacy /my-exercise redirect for participants */}
      <Route path="/my-exercise" element={
        <ProtectedRoute allowedRoles={['participant']}>
          <ParticipantPhonePage />
        </ProtectedRoute>
      } />
      
      <Route path="/exercises/:exerciseId" element={
        <ProtectedRoute>
          <ExerciseDetailPage />
        </ProtectedRoute>
      } />
      
      <Route path="/control-room/:exerciseId" element={
        <ProtectedRoute allowedRoles={['super_admin', 'simcel']}>
          <ControlRoomPage />
        </ProtectedRoute>
      } />
      
      <Route path="/users" element={
        <ProtectedRoute allowedRoles={['super_admin', 'hub_admin']}>
          <UsersPage />
        </ProtectedRoute>
      } />

      <Route path="/organisation" element={
        <ProtectedRoute allowedRoles={['super_admin']}>
          <OrganisationPage />
        </ProtectedRoute>
      } />
      

      <Route path="/objectives" element={
        <ProtectedRoute allowedRoles={['super_admin','simcel']}>
          <ObjectivesPage />
        </ProtectedRoute>
      } />

      <Route path="/inject-database" element={
        <ProtectedRoute allowedRoles={['super_admin','simcel']}>
          <InjectDatabasePage />
        </ProtectedRoute>
      } />

      <Route path="/injects" element={
        <ProtectedRoute allowedRoles={['super_admin', 'simcel', 'observer']}>
          <InjectsPage />
        </ProtectedRoute>
      } />
      
      <Route path="/reports" element={
        <ProtectedRoute allowedRoles={['super_admin', 'simcel']}>
          <ReportsPage />
        </ProtectedRoute>
      } />
      
      <Route path="/audit-logs" element={
        <ProtectedRoute allowedRoles={['super_admin']}>
          <AuditLogsPage />
        </ProtectedRoute>
      } />
      
      <Route path="/settings" element={
        <ProtectedRoute allowedRoles={['super_admin', 'simcel', 'participant', 'observer']}>
          <SettingsPage />
        </ProtectedRoute>
      } />

      <Route path="/messages" element={
        <ProtectedRoute allowedRoles={['super_admin', 'simcel', 'observer']}>
          <MessagesPage />
        </ProtectedRoute>
      } />

      <Route path="/pbx-admin/:exerciseId" element={
        <ProtectedRoute allowedRoles={['super_admin', 'simcel']}>
          <PBXAdminPage />
        </ProtectedRoute>
      } />

      <Route path="/media-portal" element={
        <ProtectedRoute allowedRoles={['super_admin', 'simcel', 'hub_admin']}>
          <MediaPortalPage />
        </ProtectedRoute>
      } />

      <Route path="/hub" element={
        <ProtectedRoute>
          <HubDashboard />
        </ProtectedRoute>
      } />

      <Route path="/hub/settings" element={
        <ProtectedRoute allowedRoles={['hub_admin', 'super_admin']}>
          <HubSettingsPage />
        </ProtectedRoute>
      } />

      <Route path="/hub/users" element={
        <ProtectedRoute allowedRoles={['super_admin', 'hub_admin']}>
          <HubUsersPage />
        </ProtectedRoute>
      } />
      <Route path="/hub/system" element={
        <ProtectedRoute allowedRoles={['super_admin', 'hub_admin']}>
          <HubSystemPage />
        </ProtectedRoute>
      } />
      <Route path="/hub/connections" element={
        <ProtectedRoute allowedRoles={['super_admin', 'hub_admin']}>
          <ConnectionHealthPage />
        </ProtectedRoute>
      } />
      <Route path="/hub/phone-numbers" element={
        <ProtectedRoute allowedRoles={['super_admin', 'hub_admin']}>
          <PhoneNumbersPage />
        </ProtectedRoute>
      } />
      <Route path="/hub/sip-extensions" element={
        <ProtectedRoute allowedRoles={['super_admin', 'hub_admin']}>
          <SipExtensionsPage />
        </ProtectedRoute>
      } />
      <Route path="/hub/sip-call-logs" element={
        <ProtectedRoute allowedRoles={['super_admin', 'hub_admin', 'simcel']}>
          <SipCallLogsPage />
        </ProtectedRoute>
      } />
      <Route path="/hub/upload-quarantine" element={
        <ProtectedRoute allowedRoles={['super_admin', 'hub_admin']}>
          <UploadQuarantinePage />
        </ProtectedRoute>
      } />
      <Route path="/exercises/:exerciseId/preflight" element={
        <ProtectedRoute allowedRoles={['super_admin', 'hub_admin', 'simcel']}>
          <WorkshopPreflightPage />
        </ProtectedRoute>
      } />
      <Route path="/exercises/:exerciseId/live" element={
        <ProtectedRoute allowedRoles={['super_admin', 'hub_admin', 'simcel']}>
          <LiveWorkshopDashboard />
        </ProtectedRoute>
      } />

      {/* Media Portal standalone pages — V14.7 EMS-aligned roles.
          allowedRoles is checked against EMS user.roles, allowedMpRoles against
          user.mp_roles. We accept both new roles (super_admin/simcel/observer/
          participant) and legacy MP roles (admin/author/viewer/user) so older
          tokens keep working. */}
      <Route path="/mp/dashboard" element={
        <ProtectedRoute
          allowedRoles={['super_admin', 'hub_admin', 'simcel']}
          allowedMpRoles={['super_admin', 'simcel', 'admin', 'author']}>
          <MPDashboardPage />
        </ProtectedRoute>
      } />
      <Route path="/mp/exercises" element={
        <ProtectedRoute
          allowedRoles={['super_admin', 'hub_admin', 'simcel', 'observer']}
          allowedMpRoles={['super_admin', 'simcel', 'observer', 'admin', 'author']}>
          <MPExercisesPage />
        </ProtectedRoute>
      } />
      <Route path="/mp/exercises/:exerciseId" element={
        <ProtectedRoute
          allowedRoles={['super_admin', 'hub_admin', 'simcel', 'observer']}
          allowedMpRoles={['super_admin', 'simcel', 'observer', 'admin', 'author']}>
          <MPExerciseDashboard />
        </ProtectedRoute>
      } />
      <Route path="/mp/social-posts" element={
        <ProtectedRoute
          allowedRoles={['super_admin', 'hub_admin', 'simcel', 'observer']}
          allowedMpRoles={['super_admin', 'simcel', 'observer', 'admin', 'author']}>
          <MPSocialPostsPage />
        </ProtectedRoute>
      } />
      <Route path="/mp/news-posts" element={
        <ProtectedRoute
          allowedRoles={['super_admin', 'hub_admin', 'simcel', 'observer']}
          allowedMpRoles={['super_admin', 'simcel', 'observer', 'admin', 'author']}>
          <MPNewsPostsPage />
        </ProtectedRoute>
      } />
      <Route path="/mp/media" element={
        <ProtectedRoute
          allowedRoles={['super_admin', 'hub_admin', 'simcel', 'observer']}
          allowedMpRoles={['super_admin', 'simcel', 'observer', 'admin', 'author', 'viewer']}>
          <MPMediaPage />
        </ProtectedRoute>
      } />
      <Route path="/mp/platforms" element={
        <ProtectedRoute
          allowedRoles={['super_admin', 'hub_admin', 'simcel']}
          allowedMpRoles={['super_admin', 'simcel', 'admin', 'author']}>
          <MPPlatformsPage />
        </ProtectedRoute>
      } />
      <Route path="/mp/users" element={
        <ProtectedRoute
          allowedRoles={['super_admin', 'hub_admin']}
          allowedMpRoles={['super_admin', 'admin']}>
          <MPUsersPage />
        </ProtectedRoute>
      } />
      <Route path="/mp/audit" element={
        <ProtectedRoute
          allowedRoles={['super_admin', 'hub_admin']}
          allowedMpRoles={['super_admin', 'admin']}>
          <MPAuditPage />
        </ProtectedRoute>
      } />
      <Route path="/mp/settings" element={
        <ProtectedRoute
          allowedRoles={['super_admin', 'hub_admin', 'simcel', 'observer']}
          allowedMpRoles={['super_admin', 'simcel', 'observer', 'admin', 'author', 'viewer']}>
          <MPSettingsPage />
        </ProtectedRoute>
      } />
      <Route path="/mp/public-display" element={
        <ProtectedRoute
          allowedRoles={['super_admin', 'hub_admin', 'simcel', 'observer', 'participant']}
          allowedMpRoles={['super_admin', 'simcel', 'observer', 'participant', 'admin', 'author', 'viewer', 'user']}>
          <MPPublicDisplayPage />
        </ProtectedRoute>
      } />

      {/* Public routes for Media Portal */}
      <Route path="/article/:postId" element={<MPArticleDetailPage />} />
      <Route path="/guest/:guestToken" element={<MPGuestPublicDisplayPage />} />

      {/* V15.0 — Logistics & Spend Command. Sibling app in the Hub.
          Route guards mirror MP: hub admins + simcel + observers + participants.
          The role scoping happens at the API layer (assigned_* on the exercise). */}
      <Route path="/hub/logistics" element={
        <ProtectedRoute allowedRoles={['super_admin', 'hub_admin', 'simcel', 'observer', 'participant']}>
          <ExerciseManagementPage />
        </ProtectedRoute>
      } />
      <Route path="/hub/logistics/home" element={
        <ProtectedRoute allowedRoles={['super_admin', 'hub_admin', 'simcel', 'observer', 'participant']}>
          <LogisticsHomePage />
        </ProtectedRoute>
      } />
      <Route path="/hub/logistics/wallet" element={
        <ProtectedRoute allowedRoles={['super_admin', 'hub_admin', 'simcel', 'observer', 'participant']}>
          <LogisticsWalletPage />
        </ProtectedRoute>
      } />
      <Route path="/hub/logistics/accommodation" element={
        <ProtectedRoute allowedRoles={['super_admin', 'hub_admin', 'simcel', 'observer', 'participant']}>
          <LogisticsAccommodationPage />
        </ProtectedRoute>
      } />
      <Route path="/hub/logistics/food" element={
        <ProtectedRoute allowedRoles={['super_admin', 'hub_admin', 'simcel', 'observer', 'participant']}>
          <LogisticsFoodPage />
        </ProtectedRoute>
      } />
      <Route path="/hub/logistics/transport" element={
        <ProtectedRoute allowedRoles={['super_admin', 'hub_admin', 'simcel', 'observer', 'participant']}>
          <LogisticsTransportPage />
        </ProtectedRoute>
      } />
      <Route path="/hub/logistics/flights" element={
        <ProtectedRoute allowedRoles={['super_admin', 'hub_admin', 'simcel', 'observer', 'participant']}>
          <LogisticsFlightsPage />
        </ProtectedRoute>
      } />
      <Route path="/hub/logistics/procurement" element={
        <ProtectedRoute allowedRoles={['super_admin', 'hub_admin', 'simcel', 'observer', 'participant']}>
          <LogisticsProcurementPage />
        </ProtectedRoute>
      } />

      <Route path="/" element={<Navigate to="/hub" replace />} />
      <Route path="*" element={<NotFoundPage />} />
    </Routes>
  );
};

function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <BrandingProvider>
          <LogoProvider>
          <WebSocketProvider>
            <BrowserRouter>
              <PBXProvider>
                <SIPProvider>
                  <AppRouter />
                  <FloatingPhone />
                </SIPProvider>
              </PBXProvider>
              <Toaster 
              position="top-right" 
              richColors 
              closeButton
              toastOptions={{
                className: 'font-primary'
              }}
            />
          </BrowserRouter>
        </WebSocketProvider>
          </LogoProvider>
        </BrandingProvider>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
